import { FileCode, ShieldCheck, Zap } from 'lucide-react';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/key-management/specification')({
  component: SpecificationTab,
});


export function SpecificationTab() {
  return (
    <div className="space-y-6">
      {/* Introduction Header */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <h2 className="text-xl font-bold mb-2">API Documentation</h2>
        <p className="text-muted-foreground mb-6">
          Integrate CreditNova's automated credit analysis directly into your own workflow using our REST API.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="p-4 rounded-xl border border-border bg-muted/30">
            <ShieldCheck className="w-5 h-5 text-[#FF4500] mb-2" />
            <h3 className="font-semibold text-sm">Secure Access</h3>
            <p className="text-xs text-muted-foreground">Authenticated via SHA-256 hashed secret keys.</p>
          </div>
          <div className="p-4 rounded-xl border border-border bg-muted/30">
            <Zap className="w-5 h-5 text-[#FF4500] mb-2" />
            <h3 className="font-semibold text-sm">High Performance</h3>
            <p className="text-xs text-muted-foreground">Standard rate limit of 1,000 requests per minute.</p>
          </div>
          <div className="p-4 rounded-xl border border-border bg-muted/30">
            <FileCode className="w-5 h-5 text-[#FF4500] mb-2" />
            <h3 className="font-semibold text-sm">JSON Standard</h3>
            <p className="text-xs text-muted-foreground">Clean, structured data responses for easy parsing.</p>
          </div>
        </div>

        <hr className="border-border mb-8" />

        {/* Getting Started Section */}
        <div className="space-y-8">
          <section>
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <div className="bg-[#FF4500] text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">1</div>
              Authentication
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Include your full API key in the <code className="bg-muted px-1.5 py-0.5 rounded text-foreground">x-api-key</code> header for every request. 
              API keys use a <code className="text-[#FF4500]">publicKey.secretKey</code> format.
            </p>
            <div className="bg-zinc-950 rounded-xl p-4 font-mono text-xs text-zinc-300 overflow-x-auto">
              <p className="text-zinc-500 mb-2">// Example Header</p>
              <p>x-api-key: creditNova_sk__abc123.secret_xyz456...</p>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <div className="bg-[#FF4500] text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">2</div>
              Base URL
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              All public API requests should be made to the following base endpoint:
            </p>
            <div className="bg-muted p-3 rounded-lg border border-border font-mono text-sm">
              https://api.creditnova.com/api/v1/public
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <div className="bg-[#FF4500] text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">3</div>
              Example Request
            </h3>
            <div className="bg-zinc-950 rounded-xl p-4 font-mono text-xs text-zinc-300 overflow-x-auto">
              <div className="flex justify-between items-center mb-4 border-b border-zinc-800 pb-2">
                <span className="text-zinc-500">POST /reports/generate</span>
                <span className="text-green-400">cURL</span>
              </div>
              <pre>
{`curl -X POST https://api.creditnova.com/api/v2/public/reports/generate \\
  -H "x-api-key: creditNova_sk_YOUR_KEY_HERE" \\
  -H "Content-Type: application/json" \\
  -d '{
    "businessName": "Acme Corp",
    "templateId": "uuid-0000-1111"
  }'`}
              </pre>
            </div>
          </section>
        </div>

        {/* Footer Link */}
        <div className="mt-10 p-4 rounded-xl bg-[#FF4500]/5 border border-[#FF4500]/20 flex items-center justify-between">
            <p className="text-sm text-foreground">
                <strong>Need a custom integration?</strong> Contact our support for specialized API permissions.
            </p>
            <button className="text-sm font-bold text-[#FF4500] hover:underline">
                Contact Support
            </button>
        </div>
      </div>
    </div>
  );
}